import React, { useMemo, useState } from 'react';
import Drilldown from '../../components/Drilldown';
import { TEAM_LABELS } from '../../config';
import { fmtMoney, fmtNumber, useFetch } from '../../hooks';
import { Purpose, Alert, Card, Segmented, Select, Skeleton, Stat } from '../../ui/basics';
import { LineChart } from '../../ui/charts';

const GROUPS = [['none', 'All placements'], ['lead', 'Team'], ['client', 'Client'], ['recruiter', 'Recruiter'], ['source', 'Source']];
const DRILLABLE = ['none', 'lead', 'client', 'recruiter'];
const pad = (n) => String(n).padStart(2, '0');

/** First and last day (YYYY-MM-DD) of a period such as "2025" or "2025 Q3". */
function bounds(period) {
  const [year, quarter] = period.split(' Q');
  if (!quarter) return [`${year}-01-01`, `${year}-12-31`];
  const first = (Number(quarter) - 1) * 3 + 1;
  const last = new Date(Number(year), first + 2, 0).getDate();
  return [`${year}-${pad(first)}-01`, `${year}-${pad(first + 2)}-${pad(last)}`];
}

export default function MarginTrend({ org, lead }) {
  const currentYear = new Date().getFullYear();
  const [period, setPeriod] = useState('year');
  const [groupBy, setGroupBy] = useState('none');
  const [fromYear, setFromYear] = useState(currentYear - 5);
  const [drill, setDrill] = useState(null);
  const groups = org === 'combined' ? [...GROUPS, ['organization', 'Organization']] : GROUPS;
  const { data, loading, error } = useFetch('analytics/margin-trend/', { organization: org, lead, period, group_by: groupBy, from_year: fromYear, top: 6, min_n: 5 });
  const name = (g) => (groupBy === 'lead' ? TEAM_LABELS[g] || g : g);
  const change = data && data.change;
  const last = data && [...data.overall].reverse().find((p) => p.period !== data.current_period && p.margin !== null);

  const series = useMemo(() => (data ? data.series.map((s) => ({ id: name(s.group), group: s.group, data: s.points.map((p) => ({ x: p.period, y: p.margin })) })) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]);
  const rates = useMemo(() => (data ? [
    { id: 'Average bill rate', data: data.overall.map((p) => ({ x: p.period, y: p.bill })) },
    { id: 'Average pay rate', data: data.overall.map((p) => ({ x: p.period, y: p.pay })) },
  ] : []), [data]);

  const onPoint = (point) => {
    if (!DRILLABLE.includes(groupBy)) return;
    const group = series.find((s) => s.id === point.serieId).group;
    const [from, to] = bounds(String(point.data.x));
    setDrill({ title: `${groupBy === 'none' ? 'Placements' : name(group)} that started in ${point.data.x}`, filters: { joined_from: from, joined_to: to, ...(groupBy === 'client' || groupBy === 'recruiter' ? { [groupBy]: group } : {}) }, lead: groupBy === 'lead' ? group : lead });
  };

  return (
    <div className="stack">
      <Purpose what="Shows whether the margin earned per hour on new placements is rising or falling over time, and why (bill rate versus pay rate)." use="Group by team, client, recruiter or source to see where margin is shrinking; click a point to list those placements." care="Margin here is per hour (bill rate minus pay rate), not total dollars. Periods with very few placements are not drawn." />
      <Card title="Margin trend" subtitle="Average hourly margin (bill rate minus pay rate) of placements, by the period they started in."
        actions={<div className="row">
          <Segmented value={period} onChange={setPeriod} options={[{ value: 'year', label: 'Yearly' }, { value: 'quarter', label: 'Quarterly' }]} />
          <Select value={fromYear} onChange={(e) => setFromYear(Number(e.target.value))} aria-label="From year"
            options={Array.from({ length: 9 }, (_, i) => currentYear - i - 1).map((y) => ({ value: y, label: `From ${y}` }))} />
        </div>}>
        {error && <Alert>{error}</Alert>}
        {!data ? <Skeleton h={70} /> : (
          <div className="grid cols-4">
            <Stat label={last ? `Average margin ${last.period}` : 'Average margin'} value={last ? `${fmtMoney(last.margin)}/hr` : '—'} sub="latest complete period" />
            <Stat label="Change" value={change && change.percent !== null ? `${change.percent > 0 ? '+' : ''}${change.percent}%` : '—'}
              sub={change ? `${fmtMoney(change.from_margin)} in ${change.from} to ${fmtMoney(change.to_margin)} in ${change.to}` : 'not enough periods with data'} />
            <Stat label="Bill rate" value={last && last.bill ? fmtMoney(last.bill) : '—'} sub="average, latest complete period" />
            <Stat label="Pay rate" value={last && last.pay ? fmtMoney(last.pay) : '—'} sub="average, latest complete period" />
          </div>
        )}
      </Card>

      <Card title="Average hourly margin" subtitle={DRILLABLE.includes(groupBy) ? 'Click a point to see those placements. Periods with fewer than 5 placements are not drawn.' : 'Periods with fewer than 5 placements are not drawn.'}
        actions={<Segmented value={groupBy} onChange={setGroupBy} options={groups.map(([value, label]) => ({ value, label }))} />}>
        {loading && !data ? <Skeleton h={320} /> : <LineChart data={series} yFormat={(v) => fmtMoney(v)} onPointClick={onPoint} />}
      </Card>

      <div className="grid cols-2">
        <Card title="Why it moves" subtitle="Average bill rate against average pay rate. Margin shrinks when pay rises faster than bill.">
          {loading && !data ? <Skeleton h={260} /> : <LineChart height={260} data={rates} yFormat={(v) => fmtMoney(v)} />}
        </Card>
        <Card flush title="Numbers" subtitle="Average hourly margin (placements)">
          <div className="table-wrap"><table className="table" style={{ minWidth: 0 }}>
            <thead><tr><th>{groups.find(([v]) => v === groupBy)[1]}</th>{data && data.periods.map((p) => <th key={p} className="r">{p}{p === data.current_period ? '*' : ''}</th>)}</tr></thead>
            <tbody>
              {data && series.map((s, i) => (
                <tr key={s.id}><td><b>{s.id}</b></td>{data.series[i].points.map((p) => (
                  <td key={p.period} className="r num">{p.margin === null ? <span className="muted">—</span> : <>{fmtMoney(p.margin)} <span className="muted">({p.n})</span></>}</td>))}</tr>
              ))}
            </tbody>
          </table></div>
          {data && <div className="pager"><span>* current period, still filling up. {fmtNumber(data.unreadable)} placements have no readable margin and {fmtNumber(data.nonpositive)} have a margin of zero or less (see Data health).</span></div>}
        </Card>
      </div>
      {drill && <Drilldown title={drill.title} org={org} lead={drill.lead} filters={drill.filters} onClose={() => setDrill(null)} />}
    </div>
  );
}
